import { Typography } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import LocationSearchIcon from '@material-ui/icons/LocationSearching';
import WarningIcon from '@material-ui/icons/Warning';
import PropTypes from 'prop-types';
import { default as React } from 'react';
import { withRouter } from 'react-router-dom';
import CommonIriDisplay from '../application-core/CommonIriDisplay';
import CancelButton from './CancelButton';
import Errored from './Errored';
import PrimaryButton from './PrimaryButton';
import { globalStyles as styles } from './style/gstheme';
import TitledPanel from './TitledPanel';
import TitledTextField from './TitledTextField';
import { MAX_LONGIRI_LENGTH } from './utils/constants';
import { computeLabelFromType, detailedCheckIRI, makeCancelable, sanitiseText } from './utils/utils';
import GS from '../grafsync/grafsync';

class FindIdentifierDialog extends React.Component {

    state = {
        open: false,
        checking: false,
        iri: "",
        exists: false,
        isErrored: false,
        checked: false,
        isDirty: false,
        isValidIRI: false,
        componentList: [],
        resourceType: undefined
    };

    handleClickOpen = () => {
        this.setState({
            open: true,
            iri: "",
            checking: false,
            exists: false,
            checked: false,
            isErrored: false,
            isDirty: false,
            isValidIRI: false,
            componentList: [],
            resourceType: undefined
        });
        this.cancellable = null;
    };

    exitFindIRI = () => { this.setState({ open: false }); };

    doFind(payload) {
        this.cancellable = makeCancelable(GS.find(payload));
        return this.cancellable;
    }
    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    loadData(iris) {
        this.cancellable = makeCancelable(GS.loadData(iris, {}, this.props.graph, true));
        return this.cancellable;
    }

    findIRI = () => {
        this.setState({
            checking: true,
            exists: false,
            componentList: [],
            checked: false
        }, async () => {
            try {
                GS.handle("find-iri");
                const { iri } = this.state;
                const { graph, componentProps } = this.props;
                const data = await this.doFind({
                    value: iri,
                    graph
                }).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                const exists = data && data.exists;
                if (exists) {
                    let iriToLoad = data.loadValue || iri;
                    let editedIRI = null;
                    let resourceType = undefined;
                    let componentList = [];
                    await this.loadData([iriToLoad]).promise;
                    if (this.cancellable && this.cancellable.isCancelled()) return;
                    if (GS.isLoaded(iriToLoad, graph)) {
                        const data = GS.data(iriToLoad, graph);
                        if (data.iri && data.iri.length > 0) {
                            editedIRI = data.iri[data.iri.length - 1];
                        }
                        resourceType = data.type;
                        componentProps.forEach(cp => {
                            if (cp in data) {
                                const cpList = Array.isArray(data[cp]) ? data[cp] : [data[cp]];
                                cpList.forEach(i => {
                                    if (!componentList.includes(i)) {
                                        componentList.push(i);
                                    }
                                });
                            }
                        });
                    }
                    this.setState({
                        exists: exists,
                        checked: true,
                        checking: false,
                        isDirty: false,
                        componentList,
                        iriToLoad,
                        resourceType,
                        editedIRI
                    });
                }
                else {
                    this.setState({
                        checking: false,
                        checked: true,
                        isDirty: false
                    });
                }
            }
            catch (e) {
                if (e.message === "The requested IRI cannot be processed.") {
                    this.setState({
                        checking: false,
                        checked: true,
                        isDirty: false,
                        isValidIRI: false
                    });
                    return;
                }
                if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
            }

        });
    };

    handleIRIChange = event => {
        const t = event.target;
        const text = sanitiseText(t.value.trim());
        let isValidIRI = true;
        if (!detailedCheckIRI(text)) {
            isValidIRI = false;
        }
        const isDirty = text !== this.state.iri;
        this.setState({ iri: text, isValidIRI, isDirty, checked: false });
    };

    clearError = async () => this.setState({
        isErrored: false,
        checking: false,
        isDirty: false
    });

    render() {

        const { classes, graph, defaultLang, project } = this.props;
        const { iri, iriToLoad, isErrored, isValidIRI, checking, isDirty,
            exists, checked, editedIRI, resourceType, componentList } = this.state;

        if (isErrored) return <Errored data={isErrored} dataTest="fi-error" clearError={this.clearError} />;

        let icon = null;
        let additionalIcon = null;
        if (!isValidIRI && iri.length > 0) {
            icon =
                <InputAdornment position="end">
                    <Tooltip title="The value is invalid">
                        <WarningIcon data-test="warning" />
                    </Tooltip>
                </InputAdornment>;
        }

        return (
            <React.Fragment>
                <span style={{ marginRight: 6 }}>
                    <Tooltip title="Find by IRI">
                        <IconButton aria-label="Find by IRI" className={classes.smallIconButton} onClick={this.handleClickOpen}>
                            <LocationSearchIcon data-test="find-iri" fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </span>
                <Dialog
                    data-test="find-iri-dialog"
                    maxWidth="md"
                    fullWidth
                    classes={{
                        paper: classes.paper,
                        paperWidthMd: classes.mediumDialog
                    }}
                    open={this.state.open}
                    onClose={this.exitFindIRI}
                    aria-labelledby="iri-dialog-title">
                    <DialogTitle disableTypography className={classes.mainHeading} id="iri-dialog-title">Find by IRI</DialogTitle>
                    <DialogContent style={{ minHeight: '340px' }}>
                        <div data-test="find-iri">
                            <form autoComplete="off" noValidate="novalidate">
                                <TitledTextField
                                    dataTest="iri"
                                    name="iri"
                                    title="IRI"
                                    autoFocus={true}
                                    required={true}
                                    text={iri}
                                    placeholder="Enter an IRI starting with http or https"
                                    maxLength={MAX_LONGIRI_LENGTH}
                                    adornment={
                                        <div style={{ display: 'inline-flex' }}>
                                            {additionalIcon}
                                            {icon}
                                        </div>
                                    }
                                    onChange={this.handleIRIChange} />
                            </form>
                            {checked && !checking && exists ?
                                <TitledPanel margin={true} title="Resource Detail">
                                    {editedIRI && editedIRI === iri &&
                                        <Typography data-test="edited-iri-text" paragraph variant="body2">This is an edited IRI.</Typography>
                                    }
                                    {editedIRI && editedIRI !== iri &&
                                        <Typography data-test="is-edited-iri-text" paragraph variant="body2">This IRI has been edited.</Typography>
                                    }
                                    {resourceType &&
                                        <Typography paragraph data-test="is-a" variant="body2">This is a {computeLabelFromType(resourceType)}.</Typography>
                                    }
                                    <div data-test="resource-chip">
                                        <CommonIriDisplay
                                            id={iriToLoad}
                                            compact={true}
                                            isReadOnly={true}
                                            loadComponents={true}
                                            projectIRI={project}
                                            handleNotAvailable={true}
                                            graph={graph}
                                            viewLang={defaultLang}
                                            defaultLang={defaultLang} />
                                    </div>
                                </TitledPanel>
                                :
                                checked &&
                                <TitledPanel margin={true} title="Resource Detail">
                                    <Typography data-test="not-found" paragraph variant="body2">This IRI cannot be found or is not a type that can be linked to.</Typography>
                                </TitledPanel>
                            }
                            {checked && !checking && exists && componentList.length > 0 &&
                                <TitledPanel margin={true} title="Found In">
                                    <div data-test="component-list">
                                        {[...componentList].sort().map(cp =>
                                            <CommonIriDisplay
                                                id={cp}
                                                key={cp}
                                                compact={true}
                                                isReadOnly={true}
                                                loadComponents={true}
                                                projectIRI={project}
                                                handleNotAvailable={true}
                                                graph={graph}
                                                viewLang={defaultLang}
                                                defaultLang={defaultLang} />
                                        )}
                                    </div>
                                </TitledPanel>
                            }
                        </div>
                    </DialogContent>
                    <DialogActions className={classes.dialogActions}>
                        <CancelButton onClick={this.exitFindIRI} />
                        <PrimaryButton
                            disabled={!isValidIRI || checking || !isDirty}
                            onClick={this.findIRI}>Find</PrimaryButton>
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        );
    }
}

FindIdentifierDialog.defaultProps = {
    componentProps: ["inScheme"]
};

FindIdentifierDialog.propTypes = {
    project: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    history: PropTypes.object,
    classes: PropTypes.object,
    componentProps: PropTypes.array,
    schemes: PropTypes.array,
    collections: PropTypes.array,
    defaultLang: PropTypes.string
};

export default withRouter(withStyles(styles)(FindIdentifierDialog));